import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Eye, X, Trash2, Clock, CheckCircle, MessageCircle, XCircle, FileText, ExternalLink, Loader2, Building2, User, AlertTriangle, CalendarClock, Flag, Mail, RefreshCw, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { getAdminSourcingRequests, updateSourcingRequestStatus, deleteSourcingRequest, createQuoteFromSourcing, getSourcingOptions, migrateSourcingStatuses } from '../../lib/api';
import { syncHostingerInbox, getSourcingImportedEmails } from '../../lib/campaignsApi';
import { Toaster, toast } from 'sonner';
import AIExtractionPanel from './AIExtractionPanel';
import InternalWorkCard from './InternalWorkCard';
import SourcingDashboardWidgets from './SourcingDashboardWidgets';
import { useApp } from '../../context/AppContext';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Feb-2026 — Last Activity humanizer.
// Returns "Today, 9:42 AM" | "Yesterday" | "3 days ago" | "Jul 10, 2026"
// (matches admin-request tone). Full ISO always exposed via tooltip
// on the wrapping <span title>.
const formatLastActivity = (iso, lang = 'en') => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '\u2014';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.floor((startOfToday - startOfDate) / 86400000);
  const locale = lang === 'es' ? 'es-ES' : 'en-US';
  const timeStr = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  if (dayDiff === 0) return `${lang === 'es' ? 'Hoy' : 'Today'}, ${timeStr}`;
  if (dayDiff === 1) return lang === 'es' ? 'Ayer' : 'Yesterday';
  if (dayDiff > 1 && dayDiff <= 7) {
    return lang === 'es' ? `hace ${dayDiff} días` : `${dayDiff} days ago`;
  }
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Phase 6.1 · Hostinger IMAP sync button ───────────────────────
const SyncHostingerButton = () => {
  const [busy, setBusy] = useState(false);
  const handleSync = async () => {
    try {
      setBusy(true);
      const r = await syncHostingerInbox(30);
      const d = r.data || {};
      toast.success(
        `Synced last 30 days — imported ${d.imported || 0}, skipped ${d.skipped_duplicates || 0}, matched ${d.matched || 0}, unmatched ${d.unmatched || 0}`,
        { duration: 6000 },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handleSync}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/15 text-white/75 text-xs hover:text-white hover:bg-white/10 disabled:opacity-50"
      data-testid="btn-sync-hostinger"
      title="Pull the last 30 days of sales@luxortek.com INBOX + Sent (read-only)"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
      Sync Hostinger Email
    </button>
  );
};

// ─── Phase D2 · Migrate legacy statuses to workflow v2 ────────────
const MigrateWorkflowButton = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const handleMigrate = async () => {
    if (!window.confirm(
      'Migrate all sourcing requests to Workflow v2?\n\n'
      + 'This safely remaps legacy statuses (in_review, options_sent, '
      + 'converted_to_quote, etc.) to the new 12-stage pipeline and '
      + 'backfills stage_entered_at / proposal_sent_at. Idempotent — '
      + 'safe to run multiple times.'
    )) return;
    try {
      setBusy(true);
      const r = await migrateSourcingStatuses(false);
      const d = r.data || {};
      const transitions = Object.entries(d.transitions || {})
        .map(([k, v]) => `${k}: ${v}`).join(' • ') || 'no status remaps needed';
      toast.success(
        `Migrated ${d.modified}/${d.total} records — ${transitions}`,
        { duration: 8000 },
      );
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handleMigrate}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs hover:bg-amber-500/20 disabled:opacity-50"
      data-testid="btn-migrate-workflow-v2"
      title="Remap legacy sourcing statuses to Workflow v2 (idempotent)"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
      Migrate Workflow v2
    </button>
  );
};

// ─── Phase 6.1 · Imported-emails panel inside Sourcing detail ─────
const ImportedEmailsPanel = ({ sourcingRequestId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!sourcingRequestId) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await getSourcingImportedEmails(sourcingRequestId);
        if (alive) setItems(r.data.items || []);
      } catch (e) {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sourcingRequestId]);

  if (loading) return (
    <div className="border border-white/10 bg-[#050505] p-4 text-white/40 text-xs">Loading email history…</div>
  );
  if (items.length === 0) return (
    <div className="border border-white/10 bg-[#050505] p-4 text-white/40 text-xs flex items-center gap-2">
      <Mail className="w-3.5 h-3.5" /> No imported emails matched to this request yet. Click <strong className="text-white/65">Sync Hostinger Email</strong> on the toolbar to pull the last 30 days.
    </div>
  );

  return (
    <div className="border border-white/10 bg-[#050505] p-4 space-y-2" data-testid="sourcing-imported-emails">
      <div className="text-xs text-white/40 uppercase tracking-wider mb-1 flex items-center gap-2">
        <Mail className="w-3.5 h-3.5" /> Email History ({items.length})
      </div>
      {items.map((it, i) => {
        const isOpen = !!expanded[it.id];
        const when = it.received_at || it.sent_at || it.created_at;
        const dirCls = it.direction === 'inbound'
          ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10'
          : 'text-[#00D4FF] border-[#00D4FF]/30 bg-[#00D4FF]/10';
        const preview = (it.body_text || '').slice(0, 220).replace(/\s+/g, ' ').trim();
        return (
          <div key={it.id} className="border border-white/5 px-3 py-2 text-xs space-y-1" data-testid={`imported-email-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-widest ${dirCls}`}>{it.direction}</span>
              <span className="text-white/50">{when ? formatDate(when) : '—'}</span>
              <span className="text-white/30">·</span>
              <span className="text-white/65 truncate max-w-[400px]">
                {it.direction === 'inbound' ? `from ${it.from_email}` : `to ${(it.to_emails || []).join(', ')}`}
              </span>
            </div>
            <div className="text-white/90 font-medium truncate">{it.subject || <em className="text-white/30">(no subject)</em>}</div>
            <div className="text-white/55 whitespace-pre-wrap break-words">
              {isOpen ? (it.body_text || '(empty body)') : (preview || '(empty body)')}
              {(it.body_text || '').length > 220 && (
                <button
                  onClick={() => setExpanded((p) => ({ ...p, [it.id]: !p[it.id] }))}
                  className="ml-2 text-[#00D4FF] hover:underline inline-flex items-center gap-1"
                  data-testid={`expand-imported-${i}`}
                >
                  {isOpen ? <><ChevronUp className="w-3 h-3" /> Less</> : <><ChevronDown className="w-3 h-3" /> More</>}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Phase D2 (workflow v2) — canonical status set + legacy alias
// entries so unmigrated docs still render a sensible badge.
const STATUS_CONFIG = {
  // v2 canonical
  new_request: { label: 'New Request', color: 'bg-[#00D4FF]/20 text-[#00D4FF]' },
  qualification: { label: 'Qualification', color: 'bg-yellow-500/20 text-yellow-300' },
  awaiting_customer_specs: { label: 'Awaiting Customer Specs', color: 'bg-orange-500/20 text-orange-300' },
  sourcing_inventory: { label: 'Sourcing Inventory', color: 'bg-purple-500/20 text-purple-300' },
  proposal_preparation: { label: 'Proposal Preparation', color: 'bg-fuchsia-500/20 text-fuchsia-300' },
  proposal_sent: { label: 'Proposal Sent', color: 'bg-cyan-500/20 text-cyan-300' },
  follow_up: { label: 'Follow-up', color: 'bg-blue-500/20 text-blue-300' },
  negotiation: { label: 'Negotiation', color: 'bg-amber-500/20 text-amber-300' },
  ready_to_close: { label: 'Ready to Close', color: 'bg-teal-500/20 text-teal-300' },
  won: { label: 'Won', color: 'bg-green-500/20 text-green-300' },
  lost: { label: 'Lost', color: 'bg-red-500/20 text-red-300' },
  on_hold: { label: 'On Hold', color: 'bg-white/10 text-white/60' },
  // Legacy fallbacks (still rendered until migration runs)
  new: { label: 'New Request', color: 'bg-[#00D4FF]/20 text-[#00D4FF]' },
  reviewing: { label: 'Qualification', color: 'bg-yellow-500/20 text-yellow-300' },
  sourcing: { label: 'Sourcing Inventory', color: 'bg-purple-500/20 text-purple-300' },
  options_sent: { label: 'Proposal Sent', color: 'bg-cyan-500/20 text-cyan-300' },
  customer_reviewing: { label: 'Follow-up', color: 'bg-blue-500/20 text-blue-300' },
  negotiating: { label: 'Negotiation', color: 'bg-amber-500/20 text-amber-300' },
  converted_to_quote: { label: 'Proposal Sent', color: 'bg-cyan-500/20 text-cyan-300' },
  closed_won: { label: 'Won', color: 'bg-green-500/20 text-green-300' },
  closed_lost: { label: 'Lost', color: 'bg-red-500/20 text-red-300' },
  not_qualified: { label: 'Lost', color: 'bg-red-500/20 text-red-300' },
  in_review: { label: 'Qualification', color: 'bg-yellow-500/20 text-yellow-300' },
  quoted: { label: 'Proposal Sent', color: 'bg-cyan-500/20 text-cyan-300' },
  closed: { label: 'Lost', color: 'bg-red-500/20 text-red-300' },
  lost_legacy: { label: 'Lost', color: 'bg-red-500/20 text-red-300' },
  reviewed: { label: 'Qualification', color: 'bg-yellow-500/20 text-yellow-300' },
  contacted: { label: 'Qualification', color: 'bg-yellow-500/20 text-yellow-300' },
};

const LOSS_REASONS = [
  'Price', 'Inventory Unavailable', 'Purchased Elsewhere', 'No Budget',
  'Customer Inactive', 'Project Cancelled', 'Not Qualified', 'Other',
];

const V2_STATUSES = [
  'new_request', 'qualification', 'awaiting_customer_specs',
  'sourcing_inventory', 'proposal_preparation', 'proposal_sent',
  'follow_up', 'negotiation', 'ready_to_close',
  'won', 'lost', 'on_hold',
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return <span className={`px-2 py-1 text-xs font-medium ${cfg.color}`} data-testid={`status-badge-${status}`}>{cfg.label}</span>;
};

const FollowUpBadge = ({ bucket, date }) => {
  if (!bucket || bucket === 'none') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-white/5 text-white/40 border border-white/10" title="No follow-up scheduled" data-testid="followup-none">
        — no follow-up
      </span>
    );
  }
  if (bucket === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-red-500/15 text-red-300 border border-red-500/40" data-testid="followup-overdue">
        <AlertTriangle className="w-3 h-3" /> Overdue {date && `(${date})`}
      </span>
    );
  }
  if (bucket === 'due_today') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid="followup-due-today">
        <CalendarClock className="w-3 h-3" /> Due Today
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" data-testid="followup-upcoming">
      <CalendarClock className="w-3 h-3" /> {date}
    </span>
  );
};

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', color: 'text-red-300' },
  high: { label: 'High', color: 'text-amber-300' },
  normal: { label: 'Normal', color: 'text-white/60' },
  low: { label: 'Low', color: 'text-white/35' },
};

/**
 * Phase D2 — inline aging chips shown next to each sourcing row.
 * Amber ≥ 7 days, red ≥ 14 days. Renders nothing when the underlying
 * timestamp is missing so we never show misleading zeros for records
 * that pre-date Phase D2 tracking.
 */
const AgingChips = ({ daysInStage, daysSinceReply, daysSinceContact, daysSinceProposal }) => {
  const tone = (n) => {
    if (n == null) return null;
    if (n >= 14) return 'bg-red-500/15 text-red-300 border-red-500/40';
    if (n >= 7) return 'bg-amber-500/15 text-amber-300 border-amber-500/40';
    return 'bg-white/5 text-white/50 border-white/10';
  };
  const chips = [];
  if (daysInStage != null) {
    chips.push({ key: 'stage', label: `${daysInStage}d in stage`, title: 'Days since this stage was entered', tone: tone(daysInStage) });
  }
  if (daysSinceContact != null) {
    chips.push({ key: 'contact', label: `${daysSinceContact}d no contact`, title: 'Days since our last outbound email', tone: tone(daysSinceContact) });
  }
  if (daysSinceReply != null) {
    chips.push({ key: 'reply', label: `${daysSinceReply}d no reply`, title: 'Days since last customer reply', tone: tone(daysSinceReply) });
  }
  if (daysSinceProposal != null) {
    chips.push({ key: 'proposal', label: `${daysSinceProposal}d prop.`, title: 'Days since proposal sent', tone: tone(daysSinceProposal) });
  }
  if (!chips.length) return <span className="text-white/25 text-[11px]">—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid="row-aging-chips">
      {chips.map(c => (
        <span
          key={c.key}
          title={c.title}
          className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium border ${c.tone}`}
          data-testid={`aging-chip-${c.key}`}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
};

export const AdminSourcingRequests = () => {
  const navigate = useNavigate();
  const { language } = useApp();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [followUpFilter, setFollowUpFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  // Feb-2026 (Phase C) — persisted sort + quick views
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem('sourcing_sort_key') || ''; } catch { return ''; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem('sourcing_sort_dir') || 'desc'; } catch { return 'desc'; }
  });
  const [opts, setOpts] = useState({ statuses: [], priorities: [], countries: [], languages: [], admin_users: [] });
  const [selected, setSelected] = useState(null);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Download all sourcing requests as .xlsx. Respects the current
  // filter set by default; pass includeAll=true to bypass filters.
  const downloadSourcingExport = async (includeAll = false) => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (!includeAll) {
        if (statusFilter) params.set('status', statusFilter);
        if (priorityFilter) params.set('priority', priorityFilter);
        if (assignedFilter) params.set('assigned_to', assignedFilter);
        if (followUpFilter) params.set('follow_up_bucket', followUpFilter);
        if (countryFilter) params.set('country', countryFilter);
        if (languageFilter) params.set('language', languageFilter);
        if (search) params.set('search', search);
      } else {
        params.set('respect_filters', 'false');
      }
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('luxortek_admin_token') || '';
      const resp = await fetch(
        `${base}/api/admin/sourcing-requests/export.xlsx?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const cd = resp.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `LuxorTek_SourcingRequests_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(includeAll ? 'Exported all sourcing requests.' : 'Exported sourcing requests (filtered).');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to export sourcing requests.');
    } finally {
      setDownloading(false);
    }
  };

  // Persist sort selection
  useEffect(() => {
    try { localStorage.setItem('sourcing_sort_key', sortKey); localStorage.setItem('sourcing_sort_dir', sortDir); } catch { /* ignore */ }
  }, [sortKey, sortDir]);

  // Quick-view presets (apply multiple filters at once)
  const applyQuickView = (preset) => {
    const me = (() => { try { const j = JSON.parse(localStorage.getItem('luxortek_admin') || '{}'); return j.email || ''; } catch { return ''; } })();
    const blanks = { statusFilter: '', priorityFilter: '', assignedFilter: '', followUpFilter: '', countryFilter: '', languageFilter: '' };
    const map = {
      mine: { ...blanks, assignedFilter: me },
      overdue: { ...blanks, followUpFilter: 'overdue' },
      due_today: { ...blanks, followUpFilter: 'due_today' },
      high_priority: { ...blanks, priorityFilter: 'high' },
      unassigned: { ...blanks, assignedFilter: '__unassigned__' },
      recent: { ...blanks },
    };
    const p = map[preset];
    if (!p) return;
    setStatusFilter(p.statusFilter);
    setPriorityFilter(p.priorityFilter);
    setAssignedFilter(p.assignedFilter);
    setFollowUpFilter(p.followUpFilter);
    setCountryFilter(p.countryFilter);
    setLanguageFilter(p.languageFilter);
    if (preset === 'recent') { setSortKey('created_at'); setSortDir('desc'); }
    if (preset === 'high_priority') { setSortKey('priority'); setSortDir('asc'); }
  };

  useEffect(() => {
    getSourcingOptions().then(r => setOpts(r.data)).catch(() => {});
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (assignedFilter) params.assigned_to = assignedFilter;
      if (followUpFilter) params.follow_up_bucket = followUpFilter;
      if (countryFilter) params.country = countryFilter;
      if (languageFilter) params.language = languageFilter;
      if (search) params.search = search;
      const res = await getAdminSourcingRequests(params);
      setRequests(res.data);
      // Auto-open the detail modal if ?focus=<id> is in the URL (used by deep
      // links from the Related Sourcing Requests card on Contact/Lead pages).
      const url = new URL(window.location.href);
      const focus = url.searchParams.get('focus');
      if (focus) {
        const found = res.data.find(r => r.id === focus);
        if (found) setSelected(found);
        url.searchParams.delete('focus');
        window.history.replaceState({}, '', url.pathname);
      }
    } catch (err) {
      toast.error('Failed to load sourcing requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, assignedFilter, followUpFilter, countryFilter, languageFilter, search]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const payload = { status: newStatus };
      // Phase D2 workflow v2 — Lost requires a reason.
      if (newStatus === 'lost') {
        const promptText = `Select a Loss Reason:\n\n${LOSS_REASONS.map((r, i) => `${i + 1}. ${r}`).join('\n')}\n\nEnter the number (1-${LOSS_REASONS.length}) or the exact reason text:`;
        const raw = (window.prompt(promptText, '') || '').trim();
        if (!raw) { toast.info('Status change cancelled — no loss reason provided.'); return; }
        let chosen = raw;
        const idx = parseInt(raw, 10);
        if (!Number.isNaN(idx) && idx >= 1 && idx <= LOSS_REASONS.length) {
          chosen = LOSS_REASONS[idx - 1];
        }
        if (!LOSS_REASONS.includes(chosen)) {
          toast.error('Invalid loss reason. Status not updated.');
          return;
        }
        payload.loss_reason = chosen;
      }
      // On Hold requires a follow-up date if one isn't already set.
      if (newStatus === 'on_hold') {
        const current = requests.find(r => r.id === id);
        if (!current?.next_follow_up_date) {
          const raw = (window.prompt('On Hold requires a follow-up date. Enter YYYY-MM-DD:', '') || '').trim();
          if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            toast.error('Invalid date. Status not updated.');
            return;
          }
          payload.next_follow_up_date = raw;
        }
      }
      await updateSourcingRequestStatus(id, payload);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchRequests();
      if (selected?.id === id) {
        setSelected({
          ...selected,
          status: newStatus,
          ...(payload.loss_reason ? { loss_reason: payload.loss_reason } : {}),
          ...(payload.next_follow_up_date ? { next_follow_up_date: payload.next_follow_up_date } : {}),
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sourcing request?')) return;
    try {
      await deleteSourcingRequest(id);
      toast.success('Request deleted');
      if (selected?.id === id) setSelected(null);
      fetchRequests();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleCreateQuote = async () => {
    if (!selected) return;
    setCreatingQuote(true);
    try {
      const res = await createQuoteFromSourcing(selected.id);
      toast.success(`Draft quote ${res.data.quote_number} created`);
      // Reflect linkage + status locally so the modal updates without a refetch
      setSelected((prev) => prev ? {
        ...prev,
        status: 'quoted',
        converted_quote_id: res.data.quote_id,
        converted_quote_number: res.data.quote_number,
      } : prev);
      fetchRequests();
      // Send the admin to the new quote so they can add line items immediately
      navigate(`/admin/quotes/${res.data.quote_id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'already_converted') {
        toast.warning(detail.message || 'A quote already exists for this sourcing request.');
        setSelected((prev) => prev ? {
          ...prev,
          status: 'quoted',
          converted_quote_id: detail.quote_id,
          converted_quote_number: detail.quote_number,
        } : prev);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to create quote'));
      }
    } finally {
      setCreatingQuote(false);
    }
  };

  // Compute the displayed list — apply unassigned filter (UI-only)
  // and the user's sort selection. Default sort = the natural newest
  // ordering returned by the backend.
  const displayed = useMemo(() => {
    let list = requests;
    if (assignedFilter === '__unassigned__') {
      list = list.filter(r => !(r.assigned_to || '').trim());
    }
    if (!sortKey) {
      // Default: overdue → due_today → high_priority → new → newest.
      const bucketRank = { overdue: 0, due_today: 1, upcoming: 2, none: 3 };
      const priorityRank = { urgent: 0, high: 1, normal: 2, low: 3 };
      const statusRank = {
        new_request: 0, qualification: 1, awaiting_customer_specs: 1,
        sourcing_inventory: 1, proposal_preparation: 1, proposal_sent: 1,
        follow_up: 1, negotiation: 1, ready_to_close: 1,
        on_hold: 2, won: 3, lost: 4,
        // Legacy safety
        new: 0, reviewing: 1, sourcing: 1, options_sent: 1,
        customer_reviewing: 1, negotiating: 1, converted_to_quote: 1,
        closed_won: 3, closed_lost: 4, not_qualified: 5,
      };
      return [...list].sort((a, b) => {
        const ba = bucketRank[a.follow_up_bucket] ?? 9;
        const bb = bucketRank[b.follow_up_bucket] ?? 9;
        if (ba !== bb) return ba - bb;
        const pa = priorityRank[a.priority] ?? 4;
        const pb = priorityRank[b.priority] ?? 4;
        if (pa !== pb) return pa - pb;
        const sa = statusRank[a.status] ?? 9;
        const sb = statusRank[b.status] ?? 9;
        if (sa !== sb) return sa - sb;
        // Feb-2026 — final tiebreaker changed from created_at → last_activity_at
        // (newest activity first) so recent replies/edits float to the top.
        const av = a.last_activity_at || a.updated_at || a.created_at || '';
        const bv = b.last_activity_at || b.updated_at || b.created_at || '';
        return String(bv).localeCompare(String(av));
      });
    }
    // Explicit user sort.
    const cmp = (a, b) => {
      let av = a[sortKey] ?? '';
      let bv = b[sortKey] ?? '';
      if (sortKey === 'last_activity_at') {
        // Derived field — fall back to updated_at / created_at.
        av = a.last_activity_at || a.updated_at || a.created_at || '';
        bv = b.last_activity_at || b.updated_at || b.created_at || '';
      }
      if (sortKey === 'days_open') {
        const now = Date.now();
        av = a.created_at ? Math.floor((now - new Date(a.created_at).getTime()) / 86400000) : 0;
        bv = b.created_at ? Math.floor((now - new Date(b.created_at).getTime()) / 86400000) : 0;
      }
      if (sortKey === 'estimated_value') { av = Number(av || 0); bv = Number(bv || 0); }
      if (sortKey === 'quantity') { av = parseInt(String(av || 0), 10) || 0; bv = parseInt(String(bv || 0), 10) || 0; }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };
    return [...list].sort(cmp);
  }, [requests, assignedFilter, sortKey, sortDir]);

  const counts = {
    all: requests.length,
    new: requests.filter(r => r.status === 'new_request' || r.status === 'new').length,
    reviewing: requests.filter(r => r.status === 'qualification' || r.status === 'reviewing').length,
    sourcing: requests.filter(r => r.status === 'sourcing_inventory' || r.status === 'sourcing').length,
    converted: requests.filter(r => r.status === 'proposal_sent' || r.status === 'options_sent' || r.status === 'converted_to_quote').length,
    overdue: requests.filter(r => r.follow_up_bucket === 'overdue').length,
  };

  return (
    <div className="px-6 py-4" data-testid="admin-sourcing-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="sourcing-page-title">Sourcing Requests</h1>
          <p className="text-white/50 text-sm mt-1">Customer sales pipeline — every request from inquiry to won/lost.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sourcing Requests export — main "Download" button uses
              the currently applied filters; the "All" button next to
              it bypasses filters so you can grab the full dataset in
              one click. */}
          <div className="flex items-center" data-testid="sourcing-export-group">
            <button
              type="button"
              onClick={() => downloadSourcingExport(false)}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="download-sourcing-requests-btn"
              title="Download Sourcing Requests — respects the current filter set"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download Sourcing Requests
            </button>
            <button
              type="button"
              onClick={() => downloadSourcingExport(true)}
              disabled={downloading}
              className="inline-flex items-center px-2 py-2 text-[10px] font-semibold uppercase tracking-wider border border-l-0 border-[#00D4FF]/40 text-[#00D4FF]/70 hover:bg-[#00D4FF]/10 disabled:opacity-40"
              data-testid="download-sourcing-all-btn"
              title="Export every sourcing request (ignore current filters)"
            >
              All
            </button>
          </div>
          {/* Phase 6.1 · Hostinger IMAP sync */}
          <SyncHostingerButton />
          {/* Phase D2 · Migrate legacy workflow statuses to v2 */}
          <MigrateWorkflowButton onDone={fetchRequests} />
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 bg-[#00D4FF]/10 text-[#00D4FF]" data-testid="count-new">{counts.new} New</span>
            <span className="px-2 py-1 bg-yellow-500/10 text-yellow-300" data-testid="count-reviewing">{counts.reviewing} Reviewing</span>
            <span className="px-2 py-1 bg-purple-500/10 text-purple-300" data-testid="count-sourcing">{counts.sourcing} Sourcing</span>
            <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300" data-testid="count-converted">{counts.converted} Converted</span>
            {counts.overdue > 0 && (
              <span className="px-2 py-1 bg-red-500/15 text-red-300 border border-red-500/40" data-testid="count-overdue">{counts.overdue} Overdue</span>
            )}
          </div>
        </div>
      </div>

      {/* Phase D · Sourcing dashboard widgets */}
      <SourcingDashboardWidgets
        setStatusFilter={setStatusFilter}
        setFollowUpFilter={setFollowUpFilter}
        clearAllFilters={() => {
          setStatusFilter('');
          setPriorityFilter('');
          setAssignedFilter('');
          setFollowUpFilter('');
          setCountryFilter('');
          setLanguageFilter('');
          setSearch('');
        }}
        onOpenRequest={(id) => {
          const found = requests.find(r => r.id === id);
          if (found) {
            setSelected(found);
          } else {
            // Not in current filtered result set — bounce with focus param so
            // the fetch effect can pick it up after clearing filters.
            setStatusFilter('');
            setPriorityFilter('');
            setAssignedFilter('');
            setFollowUpFilter('');
            setCountryFilter('');
            setLanguageFilter('');
            setSearch('');
            const url = new URL(window.location.href);
            url.searchParams.set('focus', id);
            window.history.replaceState({}, '', url.toString());
          }
        }}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, company, comments..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
            data-testid="sourcing-search-input"
          />
        </div>
        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
          data-testid="sourcing-status-filter"
        >
          <option value="">All Status</option>
          {(opts.statuses || []).map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
          data-testid="sourcing-priority-filter"
        >
          <option value="">Any Priority</option>
          {(opts.priorities || []).map(p => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        <select
          value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
          data-testid="sourcing-assigned-filter"
        >
          <option value="">Any Assignee</option>
          {(opts.admin_users || []).map(e => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <select
          value={followUpFilter} onChange={(e) => setFollowUpFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
          data-testid="sourcing-followup-filter"
        >
          <option value="">Any Follow-Up</option>
          <option value="overdue">Overdue</option>
          <option value="due_today">Due Today</option>
          <option value="upcoming">Upcoming</option>
          <option value="none">No Follow-Up</option>
        </select>
        <select
          value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
          data-testid="sourcing-country-filter"
        >
          <option value="">Any Country</option>
          {(opts.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)}
          className="px-3 py-2.5 bg-[#0A0A0A] border border-white/10 text-white text-sm"
          data-testid="sourcing-language-filter"
        >
          <option value="">Any Language</option>
          {(opts.languages || []).map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {/* Quick views + sort selector */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="sourcing-quick-views-row">
        <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">Quick views:</span>
        {[
          ['mine', 'My Requests'],
          ['overdue', 'Overdue'],
          ['due_today', 'Due Today'],
          ['high_priority', 'High Priority'],
          ['unassigned', 'Unassigned'],
          ['recent', 'Recently Added'],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => applyQuickView(k)}
            className="text-xs px-2.5 py-1 border border-white/10 hover:border-[#00D4FF]/40 text-white/65 hover:text-[#00D4FF] transition-colors"
            data-testid={`quick-view-${k}`}
          >
            {label}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-[10px] uppercase tracking-wider text-white/40">Sort:</label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="px-2 py-1.5 bg-[#0A0A0A] border border-white/10 text-white text-xs"
            data-testid="sourcing-sort-key"
          >
            <option value="">Smart (overdue → priority → newest)</option>
            <option value="last_activity_at">Last Activity</option>
            <option value="created_at">Date Created</option>
            <option value="name">Customer Name</option>
            <option value="company">Company</option>
            <option value="country">Country</option>
            <option value="quantity">Quantity</option>
            <option value="status">Status</option>
            <option value="priority">Priority</option>
            <option value="next_follow_up_date">Follow-Up Date</option>
            <option value="assigned_to">Assigned User</option>
            <option value="estimated_value">Opportunity Value</option>
            <option value="days_open">Days Open</option>
            <option value="last_contacted_date">Last Contacted</option>
            <option value="expected_close_date">Expected Close</option>
          </select>
          <button
            type="button"
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="px-2 py-1.5 bg-[#0A0A0A] border border-white/10 text-white text-xs hover:border-[#00D4FF]/40"
            data-testid="sourcing-sort-dir"
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            disabled={!sortKey}
          >
            {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-white/40">Loading...</div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-16 text-white/40" data-testid="no-sourcing-requests">No sourcing requests yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sourcing-requests-table">
            <thead className="sticky top-0 z-10 bg-[#0A0A0A] shadow-[0_1px_0_rgba(255,255,255,0.05)]">
              <tr className="border-b border-white/10 text-white/50 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left" data-testid="col-header-last-activity">
                  {language === 'es' ? 'Última Actividad' : 'Last Activity'}
                </th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Country</th>
                <th className="px-4 py-3 text-left">Qty</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Aging</th>
                <th className="px-4 py-3 text-left">Priority</th>
                <th className="px-4 py-3 text-left">Follow-Up</th>
                <th className="px-4 py-3 text-left">Assigned</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((req) => {
                const isOverdue = req.follow_up_bucket === 'overdue';
                return (
                <tr
                  key={req.id}
                  className={`border-b border-white/5 hover:bg-white/5 transition-colors ${isOverdue ? 'bg-red-500/[0.04]' : ''}`}
                  style={isOverdue ? { boxShadow: 'inset 3px 0 0 0 rgb(239 68 68 / 0.6)' } : undefined}
                  data-testid={`sourcing-row-${req.id}`}
                >
                  <td className="px-4 py-3 text-white/50 text-xs whitespace-nowrap" data-testid={`last-activity-${req.id}`}>
                    <span title={formatDate(req.last_activity_at || req.updated_at || req.created_at)}>
                      {formatLastActivity(req.last_activity_at || req.updated_at || req.created_at, language)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white font-medium">{req.name}</td>
                  <td className="px-4 py-3 text-white/70">{req.company}</td>
                  <td className="px-4 py-3 text-white/70">{req.country}</td>
                  <td className="px-4 py-3 text-white/70">{req.quantity || '\u2014'}</td>
                  <td className="px-4 py-3"><StatusBadge status={req.status} /></td>
                  <td className="px-4 py-3">
                    <AgingChips
                      daysInStage={req.days_in_stage}
                      daysSinceReply={req.days_since_last_customer_reply}
                      daysSinceContact={req.days_since_last_contact}
                      daysSinceProposal={req.days_since_proposal_sent}
                    />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(() => {
                      const pc = PRIORITY_CONFIG[req.priority] || PRIORITY_CONFIG.normal;
                      return (
                        <span className={`inline-flex items-center gap-1 ${pc.color}`}>
                          <Flag className="w-3 h-3" /> {pc.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3"><FollowUpBadge bucket={req.follow_up_bucket} date={req.next_follow_up_date ? String(req.next_follow_up_date).slice(0, 10) : ''} /></td>
                  <td className="px-4 py-3 text-xs text-white/60 truncate max-w-[180px]" title={req.assigned_to || ''}>{req.assigned_to || '\u2014'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelected(req)} className="p-1.5 text-white/40 hover:text-[#00D4FF] transition-colors" title="View" data-testid={`view-sourcing-${req.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(req.id)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-sourcing-${req.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="sourcing-detail-modal">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h3 className="text-lg font-bold text-white">Sourcing Request</h3>
              <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-white/40 mb-1">Name</div>
                  <div className="text-white text-sm" data-testid="detail-name">{selected.name}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">Company</div>
                  <div className="text-white text-sm" data-testid="detail-company">{selected.company}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">Email</div>
                  <a href={`mailto:${selected.email}`} className="text-[#00D4FF] text-sm hover:underline" data-testid="detail-email">{selected.email}</a>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">WhatsApp / Phone</div>
                  <div className="text-white text-sm" data-testid="detail-phone">{selected.phone || '\u2014'}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">Country</div>
                  <div className="text-white text-sm" data-testid="detail-country">{selected.country}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">Quantity Needed</div>
                  <div className="text-white text-sm" data-testid="detail-quantity">{selected.quantity || '\u2014'}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">Submitted</div>
                  <div className="text-white/70 text-sm" data-testid="detail-submitted">{formatDate(selected.created_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">{language === 'es' ? 'Última Actividad' : 'Last Activity'}</div>
                  <div
                    className="text-white/70 text-sm"
                    data-testid="detail-last-activity"
                    title={formatDate(selected.last_activity_at || selected.updated_at || selected.created_at)}
                  >
                    {formatLastActivity(selected.last_activity_at || selected.updated_at || selected.created_at, language)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1">Status</div>
                  <StatusBadge status={selected.status} />
                </div>
                {selected.brand && (
                  <div>
                    <div className="text-xs text-white/40 mb-1">Brand Requested</div>
                    <div className="text-white text-sm" data-testid="detail-brand">{selected.brand}</div>
                  </div>
                )}
                {selected.model && (
                  <div>
                    <div className="text-xs text-white/40 mb-1">Model Requested</div>
                    <div className="text-white text-sm" data-testid="detail-model">{selected.model}</div>
                  </div>
                )}
                {selected.intended_use && (
                  <div className="col-span-2">
                    <div className="text-xs text-white/40 mb-1">Intended Use</div>
                    <div className="text-white text-sm" data-testid="detail-intended-use">{selected.intended_use}</div>
                  </div>
                )}
              </div>

              {/* Internal Work — admin-only sales pipeline tracking */}
              <InternalWorkCard
                request={selected}
                onUpdate={(updated) => {
                  setSelected(updated);
                  fetchRequests();
                }}
              />

              <div>
                <div className="text-xs text-white/40 mb-2">Requirements / Comments</div>
                <div className="p-4 bg-white/5 border border-white/10 text-white/80 text-sm whitespace-pre-wrap leading-relaxed" data-testid="detail-comments">
                  {selected.comments || '\u2014'}
                </div>
              </div>

              {/* AI Extraction */}
              <AIExtractionPanel
                kind="sourcing"
                recordId={selected.id}
                extraction={selected.extraction}
                onUpdate={(next) => setSelected({ ...selected, extraction: next })}
              />

              {/* Phase 6.1 · Imported emails (Hostinger IMAP) matched to this request */}
              <ImportedEmailsPanel sourcingRequestId={selected.id} />

              {/* Linkage IDs + Quick Links */}
              <div className="border border-white/10 bg-[#050505] p-4 space-y-2">
                <div className="text-xs text-white/40 uppercase tracking-wider mb-2">Linked Records</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">linked_contact_id:</span>
                    <span className="text-white/70 font-mono">{selected.linked_contact_id || '\u2014'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">linked_lead_id:</span>
                    <span className="text-white/70 font-mono">{selected.linked_lead_id || '\u2014'}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {selected.linked_contact_id && (
                    <button
                      onClick={() => navigate(`/admin/contacts?focus=${selected.linked_contact_id}`)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 text-white/70 text-xs hover:text-white hover:bg-white/10"
                      data-testid="btn-view-contact"
                    >
                      <User className="w-3.5 h-3.5" /> View Contact <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                  {selected.linked_lead_id && (
                    <button
                      onClick={() => navigate(`/admin/leads/${selected.linked_lead_id}`)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 text-white/70 text-xs hover:text-white hover:bg-white/10"
                      data-testid="btn-view-lead"
                    >
                      <Building2 className="w-3.5 h-3.5" /> View Lead <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                  {selected.converted_quote_id && (
                    <button
                      onClick={() => navigate(`/admin/quotes/${selected.converted_quote_id}`)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-xs hover:bg-cyan-500/25"
                      data-testid="btn-view-quote"
                    >
                      <FileText className="w-3.5 h-3.5" /> View Quote #{selected.converted_quote_number || selected.converted_quote_id}
                    </button>
                  )}
                </div>
              </div>

              {/* Create Quote action */}
              {!selected.converted_quote_id && (
                <button
                  onClick={handleCreateQuote}
                  disabled={creatingQuote}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00D4FF] text-black font-medium hover:bg-[#00D4FF]/80 disabled:opacity-40"
                  data-testid="btn-create-quote-from-sourcing"
                >
                  {creatingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {creatingQuote ? 'Creating Draft Quote…' : 'Create Quote'}
                </button>
              )}

              {/* Status Actions */}
              <div>
                <div className="text-xs text-white/40 mb-2">Update Status</div>
                <div className="flex flex-wrap gap-2">
                  {V2_STATUSES.map((key) => {
                    const cfg = STATUS_CONFIG[key];
                    return (
                      <button
                        key={key}
                        onClick={() => handleStatusChange(selected.id, key)}
                        disabled={selected.status === key}
                        className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                          selected.status === key
                            ? 'opacity-40 cursor-not-allowed border-white/10 text-white/40'
                            : `${cfg.color} border-current hover:opacity-80`
                        }`}
                        data-testid={`set-status-${key}`}
                      >
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
                {selected.status === 'lost' && selected.loss_reason && (
                  <div className="text-xs text-white/50 mt-2" data-testid="loss-reason-display">
                    Loss Reason: <span className="text-red-300 font-medium">{selected.loss_reason}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
